import { useEffect, useMemo, useState } from 'react';
import MapView from './components/MapView';
import type { AppConfigResponse, Job, JobsResponse } from './types';

const DEFAULT_DEV_API_BASE = 'http://127.0.0.1:8787';

function normalizeBase(value: string | undefined): string {
  if (!value) {
    return '';
  }

  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function resolveApiBase(): string {
  const explicitBase = normalizeBase(import.meta.env.VITE_API_BASE as string | undefined);
  if (explicitBase) {
    return explicitBase;
  }

  if (import.meta.env.DEV) {
    return normalizeBase(import.meta.env.VITE_DEV_API_BASE as string | undefined) || DEFAULT_DEV_API_BASE;
  }

  return '';
}

const API_BASE = resolveApiBase();

function buildApiUrl(path: string): string {
  return API_BASE ? `${API_BASE}${path}` : path;
}

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'Not synced yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function parseJobsResponse(raw: string): JobsResponse {
  return JSON.parse(raw) as JobsResponse;
}

function parseConfigResponse(raw: string): AppConfigResponse {
  return JSON.parse(raw) as AppConfigResponse;
}

export default function App() {
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeJobKey, setActiveJobKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadJobs() {
      setLoading(true);
      setError(null);

      try {
        const [jobsResponse, configResponse] = await Promise.all([
          fetch(buildApiUrl('/api/jobs')),
          fetch(buildApiUrl('/api/config')),
        ]);
        const [jobsRaw, configRaw] = await Promise.all([jobsResponse.text(), configResponse.text()]);

        if (!jobsResponse.ok) {
          throw new Error(`Unable to load jobs (${jobsResponse.status}). ${jobsRaw.slice(0, 160).trim()}`);
        }

        if (!configResponse.ok) {
          throw new Error(`Unable to load app config (${configResponse.status}). ${configRaw.slice(0, 160).trim()}`);
        }

        const payload = parseJobsResponse(jobsRaw);
        const config = parseConfigResponse(configRaw);

        if (cancelled) {
          return;
        }

        setGoogleMapsApiKey(config.googleMapsApiKey ?? '');
        setJobs(payload.items);
        setGeneratedAt(payload.generatedAt ?? null);
        setLastSuccessAt(payload.lastSuccessAt ?? null);

      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Unexpected error loading jobs');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadJobs();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredJobs = useMemo(() => {
    if (!searchTerm) {
      return jobs;
    }

    const normalizedSearch = searchTerm.toLowerCase();
    return jobs.filter((job) => {
      return (
        job.summary.toLowerCase().includes(normalizedSearch)
        || job.address.toLowerCase().includes(normalizedSearch)
        || job.key.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [jobs, searchTerm]);

  const mappedJobs = useMemo(() => {
    return filteredJobs.filter((job) => job.lat !== null && job.lng !== null);
  }, [filteredJobs]);

  useEffect(() => {
    if (!filteredJobs.length) {
      setActiveJobKey(null);
      return;
    }

    if (activeJobKey && !filteredJobs.some((job) => job.key === activeJobKey)) {
      setActiveJobKey(null);
    }
  }, [activeJobKey, filteredJobs]);

  const activeJob = filteredJobs.find((job) => job.key === activeJobKey) ?? null;

  return (
    <main className="app-shell">
      <section className="jobs-panel">
        <header className="panel-header">
          <p className="eyebrow">Survey history</p>
          <h1>Completed Jobs</h1>
          <p className="panel-copy">
            Every completed Jira job in one place, with the original ticket link and map location when coordinates are available.
          </p>
        </header>

        <div className="status-bar">
          <span>{filteredJobs.length} jobs shown</span>
          <span>{mappedJobs.length} shown on map</span>
          <span>Snapshot generated: {formatTimestamp(generatedAt)}</span>
          <span>Last successful sync: {formatTimestamp(lastSuccessAt)}</span>
        </div>

        <label className="search-field">
          <span className="sr-only">Search jobs</span>
          <input
            type="search"
            placeholder="Search by job name, address, or Jira key"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>

        {error ? (
          <div className="message-card error-card" role="alert">
            <strong>Unable to load jobs</strong>
            <p>{error}</p>
          </div>
        ) : null}

        {!error && loading ? (
          <div className="message-card">
            <strong>Loading jobs</strong>
            <p>Reading the latest cached snapshot.</p>
          </div>
        ) : null}

        {!loading && !filteredJobs.length ? (
          <div className="message-card">
            <strong>No jobs found</strong>
            <p>Try a different search term.</p>
          </div>
        ) : null}

        <div className="jobs-list">
          {filteredJobs.map((job) => {
            const isActive = job.key === activeJobKey;
            const hasCoordinates = job.lat !== null && job.lng !== null;

            return (
              <article
                key={job.key}
                className={`jobs-card${isActive ? ' active' : ''}`}
                onClick={() => setActiveJobKey(job.key)}
              >
                <div className="job-title-row">
                  <h2>{job.summary}</h2>
                  <span className={`map-badge${hasCoordinates ? '' : ' muted'}`}>
                    {hasCoordinates ? 'Mapped' : 'List only'}
                  </span>
                </div>

                <p className="job-key">{job.key}</p>
                <address>{job.address}</address>

                <div className="jobs-meta">
                  <span>Jira updated: {formatTimestamp(job.jiraUpdatedAt)}</span>
                </div>

                <a href={job.url} target="_blank" rel="noreferrer">
                  Open Jira ticket
                </a>
              </article>
            );
          })}
        </div>
      </section>

      <section className="map-panel" aria-label="Completed jobs map">
        <div className="map-panel-header">
          <div>
            <p className="eyebrow">Map</p>
            <h2>{activeJob?.summary ?? 'Completed jobs map'}</h2>
          </div>
          {activeJob ? (
            <a href={activeJob.url} target="_blank" rel="noreferrer" className="map-ticket-link">
              Open selected Jira ticket
            </a>
          ) : null}
        </div>

        {googleMapsApiKey ? (
          <MapView
            apiKey={googleMapsApiKey}
            jobs={mappedJobs}
            activeJobKey={activeJobKey}
            onMarkerSelect={setActiveJobKey}
          />
        ) : (
          <div className="map-empty-state">
            <strong>Google Maps key missing</strong>
            <p>Set `GOOGLE_MAPS_API_KEY` in the Worker environment to render the map.</p>
          </div>
        )}
      </section>
    </main>
  );
}
